class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setInterceptCartLinks();
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const self = this;
        if (window.DEBUG_BYOB) console.log('[BYOB Progress] cartUpdate event received, will run updateByobDrawerProgress in next tick');
        setTimeout(function () {
          self.updateByobDrawerProgress('cartUpdate');
          // BYOB already owns bundle state when it triggered this update; syncing from cart here races with cart clears and can restore the last removed item.
          if (!event || event.source !== 'byob') {
            self.notifyByobCartSync();
          }
        }, 0);
      });
    }
    this.updateByobDrawerProgress('connected');
    this.notifyByobCartSync({ onlyIfCartHasBundle: true });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  notifyByobCartSync(options) {
    try {
      document.dispatchEvent(new CustomEvent('byob:sync-from-cart', {
        detail: { onlyIfCartHasBundle: !!(options && options.onlyIfCartHasBundle) }
      }));
    } catch (e) {}
  }

  updateByobDrawerProgress(source) {
    const PREFIX = '[BYOB Progress]';
    const debug = !!window.DEBUG_BYOB;
    if (debug) console.log(PREFIX, 'updateByobDrawerProgress() called', source ? '(source: ' + source + ')' : '(source: open/direct)');

    const wrap = this.querySelector('[data-byob-progress-wrap]');
    if (!wrap) {
      const allRows = this.querySelectorAll('tr.cart-item');
      const bundleRows = this.querySelectorAll('tr.cart-item[data-bundle-item]');
      console.warn(PREFIX, 'Progress wrap NOT FOUND ([data-byob-progress-wrap]). Reason: Liquid has_bundle is false (no cart item has properties._bundle == "1"), so the progress block is not rendered.');
      console.warn(PREFIX, 'DOM state: total tr.cart-item rows =', allRows.length, ', rows with [data-bundle-item] =', bundleRows.length);
      return;
    }
    if (debug) console.log(PREFIX, 'Progress wrap found');

    if (!window.BYOBProgressBar) {
      console.warn(PREFIX, 'BYOBProgressBar not loaded (byob-progress.js)');
      return;
    }

    const serverCount = wrap.getAttribute('data-byob-server-count');
    const rows = this.querySelectorAll('tr.cart-item[data-bundle-item]');
    if (debug) console.log(PREFIX, 'Selector: tr.cart-item[data-bundle-item]', 'matched rows:', rows.length);

    var n = 0;
    rows.forEach(function (row, i) {
      const qty = parseInt(row.getAttribute('data-quantity') || '1', 10);
      n += qty;
      if (debug) console.log(PREFIX, '  Row ' + (i + 1) + ': data-quantity="' + (row.getAttribute('data-quantity') || '') + '" -> parsed ' + qty);
    });
    if (debug) console.log(PREFIX, 'Total bundle item count (from DOM):', n);

    if (n === 0 && rows.length > 0) {
      console.warn(PREFIX, 'Rows have [data-bundle-item] but data-quantity missing or 0. Check cart-drawer-line-item.liquid outputs data-quantity when item.properties._bundle == "1".');
    }
    if (n === 0 && serverCount != null && parseInt(serverCount, 10) > 0) {
      console.warn(PREFIX, 'MISMATCH: Server says', serverCount, 'bundle items but DOM has 0. Cart drawer table may have been replaced without tr.cart-item[data-bundle-item] or selector is wrong.');
    }

    var tiers = window.BYOBProgressBar.parseTiers(wrap.getAttribute('data-byob-tiers'));
    if (window.buildYourBundle && window.buildYourBundle.config && window.buildYourBundle.config.bundleTiers) {
      tiers = window.BYOBProgressBar.parseTiers(window.buildYourBundle.config.bundleTiers);
    }

    window.BYOBProgressBar.render(wrap, n, tiers);
    if (debug) console.log(PREFIX, 'Progress updated:', { n, tiers: tiers });
  }

  setInterceptCartLinks() {
    document.body.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/cart"]');
      if (!link || !link.href) return;
      const cartPath = (window.routes && window.routes.cart_url) ? window.routes.cart_url.replace(/^\//, '') : 'cart';
      try {
        const url = new URL(link.href);
        if (url.pathname === '/cart' || url.pathname === '/cart/') {
          e.preventDefault();
          this.open(link);
        }
      } catch (err) {}
    }, true);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.updateByobDrawerProgress('open');
    this.notifyByobCartSync({ onlyIfCartHasBundle: true });
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (parsedState && parsedState.sections) {
      this.refreshSections(parsedState.sections, { open: true });
      return;
    }
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;
      const inner = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (inner) sectionElement.innerHTML = inner;
    });

    setTimeout(() => {
      this.updateByobDrawerProgress();
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  refreshSections(sections, options) {
    if (!sections) return;
    options = options || {};
    const wasOpen = this.classList.contains('active');
    const cartDrawerHtml = sections['cart-drawer'];

    if (cartDrawerHtml) {
      const doc = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
      const newCartDrawer = doc.querySelector('#CartDrawer');
      const existingCartDrawer = document.getElementById('CartDrawer');
      if (newCartDrawer && existingCartDrawer) {
        existingCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }
      const newDrawerEl = doc.querySelector('cart-drawer');
      if (newDrawerEl) {
        this.classList.toggle('is-empty', newDrawerEl.classList.contains('is-empty'));
        const drawerInner = this.querySelector('.drawer__inner');
        const newInner = newDrawerEl.querySelector('.drawer__inner');
        if (drawerInner && newInner) {
          drawerInner.classList.toggle('is-empty', newInner.classList.contains('is-empty'));
        }
      }
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
    }

    if (sections['cart-icon-bubble']) {
      const bubbleHtml = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const bubbleSection = bubbleHtml.querySelector('.shopify-section') || bubbleHtml.body.firstElementChild;
      const cartIconBubble = document.querySelector('#cart-icon-bubble');
      if (cartIconBubble && bubbleSection) {
        cartIconBubble.innerHTML = bubbleSection.innerHTML;
      }
    }

    this.updateByobDrawerProgress();
    if (options.open) {
      this.open();
    } else if (!wasOpen) {
      this.classList.remove('active', 'animate');
      document.body.classList.remove('overflow-hidden');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html || typeof html !== 'string') return '';
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
